"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Building2, Database, Scale, Users as Users2, Globe as Globe2, Clock, LockKeyhole, FileCheck2, Cookie, FileText, Mail, Phone, MapPin } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const policySections = [{
  number: "1",
  title: "Who we are (data controller)",
  icon: Building2,
  content: "The data controller is Clinic Agatas Ltd., Linkuvos st. 60, Kaunas, Lithuania. Email info@clinicagatas.com. Phone (Lithuania) +370 602 26118. Phone (Irish patient coordinator) +353 871 753 061."
}, {
  number: "2",
  title: "What data we collect",
  icon: Database,
  content: "When you use our enquiry form or contact us, we collect identity and contact details (full name, email, phone), your preferred consultation date, health data (the nature and location of your pain, a description of your condition, and any medical information, scans or history you share), reimbursement data for your HSE Cross-Border Directive application, and the content of correspondence. We do not knowingly collect data from anyone under 18."
}, {
  number: "3",
  title: "Why we use your data and legal basis",
  icon: Scale,
  content: "We use your data to respond to your enquiry, provide medical consultations, treatment and post-operative care, coordinate travel and rehabilitation, prepare your HSE reimbursement documentation, keep records required by law, and communicate about your care. Legal bases under the GDPR: your explicit consent (Article 6(1)(a) and 9(2)(a)) when you submit the form, which you can withdraw at any time; provision of healthcare (Article 9(2)(h)); performance of a contract (Article 6(1)(b)); legal obligation (Article 6(1)(c)); and legitimate interests (Article 6(1)(f))."
}, {
  number: "4",
  title: "Who we share data with",
  icon: Users2,
  content: "Only where necessary: our medical team; our rehabilitation partner Egle Rehabilitation center (Birstonas); the HSE in Ireland for your Cross-Border Directive claim; your GP; and trusted processors (hosting, email) under data-processing agreements. We never sell your data or share it for advertising."
}, {
  number: "5",
  title: "International transfers",
  icon: Globe2,
  content: "Your data is processed within the EU (Lithuania and Ireland). Any transfer outside the EU/EEA uses GDPR-required safeguards."
}, {
  number: "6",
  title: "How long we keep data",
  icon: Clock,
  content: "Enquiries that do not result in treatment: 2 years, then deleted. Patient medical records: as required by Lithuanian medical-records law (longer than 2 years). Reimbursement documentation: as required by law."
}, {
  number: "7",
  title: "How we protect data",
  icon: LockKeyhole,
  content: "Encrypted (HTTPS) transmission, appropriate technical and organisational measures, access restricted to staff who need it."
}, {
  number: "8",
  title: "Your rights",
  icon: FileCheck2,
  content: "Access, rectification, erasure (subject to medical-record retention duties), restriction, objection, portability, and withdrawal of consent. Contact info@clinicagatas.com; we respond within one month. You may complain to the Data Protection Commission in Ireland (dataprotection.ie) or Valstybine duomenu apsaugos inspekcija in Lithuania (vdai.lrv.lt)."
}, {
  number: "9",
  title: "Cookies",
  icon: Cookie,
  content: "See our Cookie Policy."
}, {
  number: "10",
  title: "Changes",
  icon: FileText,
  content: "We may update this policy; the Last updated date shows the latest version."
}, {
  number: "11",
  title: "Contact",
  icon: Mail,
  content: "Clinic Agatas Ltd., Linkuvos st. 60, Kaunas, Lithuania. Email info@clinicagatas.com. Phone +370 602 26118 (LT) / +353 871 753 061 (Ireland)."
}];
export default function PrivacyPolicy() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="privacypolicycontent" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="outline" className="px-3 py-1 font-medium text-xs tracking-wide">
              <ShieldCheck className="size-3.5 text-primary mr-1" />
              Patient Data Protection
            </Badge>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold font-default tracking-tight text-primary mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm font-medium text-muted-foreground mb-6">
            Last updated: 14 September 2026
          </p>
          <div className="bg-card border border-border p-6 md:p-8 rounded-xl shadow-xs text-left">
            <p className="text-foreground leading-relaxed text-base md:text-lg">
              This Privacy Policy explains how we collect, use, store and protect your personal data when you use painreliefabroad.ie or contact us about treatment at Clinic Agatas. It applies to patients and prospective patients, including residents of Ireland using the EU Cross-Border Healthcare Directive. Because we provide healthcare services, some of the information you give us is health data (special category data) and receives the highest level of legal protection.
            </p>
          </div>
        </motion.div>

        <Separator className="my-10" />

        <div className="space-y-6">
          {policySections.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.number} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.4,
            delay: shouldReduceMotion ? 0 : index * 0.03
          }}>
                <Card className="border border-border/80 shadow-xs hover:border-primary/30 transition-[border-color,box-shadow]">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center size-8 rounded-full bg-accent text-accent-foreground font-semibold text-sm">
                        {item.number}
                      </span>
                      <CardTitle className="text-lg md:text-xl font-bold font-default text-primary flex items-center gap-2">
                        <IconComponent className="size-5 text-primary shrink-0 opacity-80" />
                        {item.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
                      {item.content}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-12 bg-accent/40 border border-accent rounded-xl p-6 md:p-8">
          <h2 className="text-lg font-bold text-primary mb-3 flex items-center gap-2">
            <Building2 className="size-5 text-primary" />
            Clinic Agatas Ltd. Data Controller Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 text-sm text-foreground">
            <div className="flex items-start gap-2">
              <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
              <span>Linkuvos st. 60, Kaunas, Lithuania</span>
            </div>
            <div className="flex items-start gap-2">
              <Mail className="size-4 text-primary shrink-0 mt-0.5" />
              <a href="mailto:info@clinicagatas.com" className="text-primary hover:underline underline-offset-4">
                info@clinicagatas.com
              </a>
            </div>
            <div className="flex items-start gap-2">
              <Phone className="size-4 text-primary shrink-0 mt-0.5" />
              <div>
                <div>
                  <a href="tel:+37060226118" className="hover:underline text-primary">
                    +370 602 26118 (LT)
                  </a>
                </div>
                <div>
                  <a href="tel:+353871753061" className="hover:underline text-primary">
                    +353 871 753 061 (IE)
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}